/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import styled from "styled-components";
import {
  Button,
  ExtendedPaginationBox,
  FlexContainer,
  Heading,
  Sidebar,
  Switch,
  Text,
  theme,
} from "@nordcloud/gnui";
import { useApplicationsPaginatedListQuery } from "~/generated/graphql";
import { LoaderWrap } from "~/components";
import { PaginationProps } from "~/models";
import { Action, ACTION, State } from "./reducers";

type ApplicationData = {
  nid: string;
  name: string;
  description?: string | null;
};

const ApplicationItem = styled.div`
  padding: ${theme.spacing.spacing03} ${theme.spacing.spacing02};
  border-bottom: 1px solid ${theme.color.border.border01};

  &:hover {
    cursor: pointer;
    background-color: ${theme.color.background.ui03};
  }
`;

type Props = {
  dispatch: (action: Action) => void;
  state: State;
};

export function ApplicationsSidebar({ dispatch, state }: Props) {
  const [appState, setAppState] = React.useState<PaginationProps>({
    page: 0,
    limit: 20,
  });
  const { data, loading } = useApplicationsPaginatedListQuery({
    variables: {
      limit: appState.limit,
      page: appState.page,
    },
  });

  const onAppSelect = (id: string) => {
    const applications = state.applications.includes(id)
      ? state.applications.filter((appId: string) => appId !== id)
      : [...state.applications, id];
    dispatch({ type: ACTION.APPS, applications });
  };

  return (
    <Sidebar
      isOpen={state.showAppSidebar}
      title="Select applications"
      footer={
        <>
          <FlexContainer justifyContent="flex-end">
            <Button
              m={theme.spacing.spacing04}
              ml="0"
              icon="plus"
              onClick={() =>
                dispatch({ type: ACTION.APP_SELECTION, show: false })
              }
            >
              Apply
            </Button>
          </FlexContainer>
        </>
      }
      onClick={() => dispatch({ type: ACTION.APP_SELECTION, show: false })}
    >
      <LoaderWrap inContent loading={loading}>
        <>
          {data?.applicationsPaginated?.results?.map(
            ({ nid, name, description: appDescription }: ApplicationData) => {
              return (
                <ApplicationItem
                  key={nid}
                  onClick={() => {
                    onAppSelect(nid);
                  }}
                >
                  <FlexContainer justifyContent="space-between">
                    <div>
                      <Heading level={4}>{`${name}`}</Heading>
                      <Text size="sm" tag="div" color={theme.color.text.text02}>
                        {appDescription || nid}
                      </Text>
                    </div>
                    <Switch
                      checked={state.applications.includes(nid)}
                      onChange={(event) => event.preventDefault()}
                      onClick={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                      }}
                    />
                  </FlexContainer>
                </ApplicationItem>
              );
            }
          )}
          <ExtendedPaginationBox
            small
            count={data?.applicationsPaginated?.count ?? 0}
            from={appState.page * appState.limit}
            setSize={(limit) => setAppState({ page: 0, limit })}
            size={appState.limit}
            setPage={(newPage) =>
              setAppState({
                ...appState,
                page: Math.ceil(newPage / appState.limit),
              })
            }
          />
        </>
      </LoaderWrap>
    </Sidebar>
  );
}
